import { useLang } from "@/lib/i18n";
import { PLATFORMS, PLATFORM_LABEL } from "@/lib/format";
import { Check, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export const Logo = ({ className = "h-10", testid = "xd-logo" }) => (
  <img src="/xd-logo.png" alt="XD" className={`${className} w-auto select-none`} draggable={false} data-testid={testid} />
);

export const StatusBadge = ({ status, className = "" }) => {
  const { t } = useLang();
  if (!status || status === "available") return null;
  return <span data-testid={`badge-${status}`} className={`xd-badge xd-badge-${status} ${className}`}>{t(`status.${status}`)}</span>;
};

export const LangSwitch = ({ testid = "lang-switch" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex border border-white/15 text-xs" data-testid={testid}>
      {["he", "ar"].map((l) => (
        <button key={l} data-testid={`${testid}-${l}`} onClick={() => setLang(l)}
          className={`px-3 py-2 transition-colors ${lang === l ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}>
          {l === "he" ? "עברית" : "العربية"}
        </button>
      ))}
    </div>
  );
};

export const RestrictionChips = ({ restrictions = {} }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap gap-2" data-testid="restriction-chips">
      {PLATFORMS.map((p) => {
        const ok = restrictions[p] !== false;
        return (
          <span key={p} data-testid={`restriction-${p}-${ok ? "allowed" : "blocked"}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border ${ok ? "border-emerald-700/60 text-emerald-300 bg-emerald-950/30" : "border-red-600 text-red-300 bg-red-950/40"}`}>
            {ok ? <Check size={14} /> : <X size={14} />}
            <span className="ltr-num">{PLATFORM_LABEL[p]}</span>
            <span className="opacity-80">· {ok ? t("restr.allowed") : t("restr.blocked")}</span>
          </span>
        );
      })}
    </div>
  );
};

export const RestrictionToggles = ({ value, onChange }) => {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {PLATFORMS.map((p) => (
        <label key={p} className="flex items-center justify-between xd-panel px-4 py-3">
          <span className="ltr-num">{PLATFORM_LABEL[p]}</span>
          <span className="flex items-center gap-3 text-sm text-zinc-400">
            {value[p] !== false ? t("restr.allowed") : t("restr.blocked")}
            <Switch data-testid={`restriction-toggle-${p}`} checked={value[p] !== false} onCheckedChange={(c) => onChange({ ...value, [p]: c })} />
          </span>
        </label>
      ))}
    </div>
  );
};

export const Section = ({ title, children, actions, testid }) => (
  <section className="xd-panel p-5 md:p-6 space-y-5 min-w-0" data-testid={testid}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center justify-between gap-3">
        {title && <h2 className="text-base md:text-lg font-medium">{title}</h2>}
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Stat = ({ label, value, testid }) => (
  <div className="xd-panel p-5" data-testid={testid}>
    <p className="eyebrow">{label}</p>
    <p className="mt-3 text-3xl font-semibold chrome-text ltr-num">{value ?? 0}</p>
  </div>
);

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4 min-w-0">
    <div className="min-w-0 max-w-full">
      {eyebrow && <p className="eyebrow mb-2 break-words">{eyebrow}</p>}
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight chrome-text break-words">{title}</h1>
    </div>
    {children}
  </div>
);

export const Empty = ({ text }) => <div className="xd-panel p-10 text-center text-zinc-500" data-testid="empty-state">{text}</div>;

export const Loader = () => (
  <div className="py-24 grid place-items-center" data-testid="loader">
    <div className="h-px w-40 xd-line animate-pulse" />
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-2">
    <span className="text-sm text-zinc-300">{label}</span>
    {children}
    {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
  </label>
);
